"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { getDatabase, ref, onValue } from "firebase/database"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import QuoteRequestForm from "./QuoteRequestForm"
import QuotationResponses from "./QuotationResponse"
import PreviousHistory from "./QuotePreviousHistory"
import ActiveSession from "./ActiveSession"
import "./QuotationManagement.css"
import { useCurrentUid } from "../hooks/useCurrentUid"
import { useAccount } from "../context/AccountContext"

const MAX_NEGOTIATIONS = 2

export default function QuotationManagement() {
  const userId = useCurrentUid()
  const { accountSwitchTrigger } = useAccount()
  const [activeTab, setActiveTab] = useState("request")
  const [quoteRequests, setQuoteRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSession, setActiveSession] = useState(null)
  const [historyVisible, setHistoryVisible] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [sessionError, setSessionError] = useState("")
  const [showHistory, setShowHistory] = useState(false)
  const [showHistoryText, setShowHistoryText] = useState(false)

  // Optimize mobile detection with debouncing
  useEffect(() => {
    let timeoutId
    const checkIfMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth < 829
        if (mobile !== isMobile) {
          setIsMobile(mobile)
        }
      }, 100)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)
    return () => {
      window.removeEventListener("resize", checkIfMobile)
      clearTimeout(timeoutId)
    }
  }, [isMobile])

  // Fix mobile history visibility
  useEffect(() => {
    if (isMobile) {
      setHistoryVisible(false)
    } else {
      setHistoryVisible(true)
    }
  }, [isMobile])

  // Main Firebase effect - FIXED to prevent infinite loops
  useEffect(() => {
    if (!userId) {
      setQuoteRequests([])
      setActiveSession(null)
      setLoading(false)
      setSessionError("No account selected.")
      return
    }

    if (!navigator.onLine) {
      setSessionError("You are offline. Please check your internet connection.")
      setLoading(false)
      return
    }

    setLoading(true)
    setSessionError("")

    const db = getDatabase()
    const requestsRef = ref(db, `quoteRequests/${userId}`)

    const unsubscribe = onValue(
      requestsRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const requestsList = Object.keys(data).map((key) => ({
              id: key,
              ...data[key],
            }))
            const updatedQuotes = requestsList.reverse() // newest first
            setQuoteRequests(updatedQuotes)

            // Keep active session in sync if it's still valid
            setActiveSession((prev) => {
              if (!prev) return null
              const updated = updatedQuotes.find(q => q.id === prev.id)
              return updated ? { ...updated } : prev
            })
          } else {
            setQuoteRequests([])
            setActiveSession(null)
          }
        } catch (error) {
          console.error("Error processing quotes:", error)
          setSessionError("Failed to load quotations.")
        } finally {
          setLoading(false)
        }
      },
      (error) => {
        console.error("Firebase error:", error)
        setSessionError("Connection error. Please try again.")
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [userId, accountSwitchTrigger])
  // Memoized callbacks to prevent unnecessary re-renders
  const setActiveQuote = useCallback(
    (quote) => {
      setActiveSession(quote)
      if (isMobile) {
        setHistoryVisible(false)
        setShowHistory(false)
        setShowHistoryText(false)
      }
    },
    [isMobile],
  )

  const endSession = useCallback(() => {
    setActiveSession(null)
    setHistoryVisible(true)
  }, [])

  const toggleHistory = useCallback(() => {
    if (isMobile) {
      if (showHistory) {
        setHistoryVisible(false)
        setShowHistory(false)
        setShowHistoryText(false)
      } else if (showHistoryText) {
        setHistoryVisible(true)
        setShowHistory(true)
      } else {
        setShowHistoryText(true)
      }
    } else {
      setHistoryVisible((prev) => !prev)
      setShowHistory((prev) => !prev)
    }
  }, [isMobile, showHistory, showHistoryText])

  const toggleHistoryText = useCallback(() => {
    setShowHistoryText((prev) => !prev)
  }, [])

  // Memoized history quotes to prevent recalculation
  const historyQuotes = useMemo(() => {
    return activeSession ? quoteRequests.filter((quote) => quote.id !== activeSession.id) : quoteRequests
  }, [activeSession, quoteRequests])

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    )
  }

  if (sessionError) {
    return <div className="error">{sessionError}</div>
  }

  return (
    <div className="quotation-management">
      <h2 className="quotation-management__title">Quotation Management</h2>

      <div className="layout-container">
        <div className={`active-session-container ${!activeSession && activeTab === "request" ? "full-width" : ""}`}>
          {activeSession ? (
            <ActiveSession
              activeSession={activeSession}
              endSession={endSession}
              toggleHistory={toggleHistory}
              isMobile={isMobile}
              userId={userId}
              MAX_NEGOTIATIONS={MAX_NEGOTIATIONS}
            />
          ) : (
            <div className={`tabs ${!historyVisible ? "full-width" : ""}`}>
              <div className="tabs__list">
                <button
                  className={`tabs__trigger ${activeTab === "responses" ? "tabs__trigger--active" : ""}`}
                  onClick={() => setActiveTab("responses")}
                >
                  Quotation Responses
                </button>
                <button
                  className={`tabs__trigger ${activeTab === "request" ? "tabs__trigger--active" : ""}`}
                  onClick={() => setActiveTab("request")}
                >
                  Request a Quote
                </button>
                {isMobile && (
                  <div className="history-menu">
                    <button
                      className="button button--icon mobile-menu-toggle"
                      onClick={toggleHistoryText}
                      title="View History"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="1"></circle>
                        <circle cx="12" cy="5" r="1"></circle>
                        <circle cx="12" cy="19" r="1"></circle>
                      </svg>
                    </button>
                    {showHistoryText && (
                      <div className="history-text-popup" onClick={toggleHistory}>
                        <span className="pre-his">Previous History</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={`tabs__content ${activeTab === "responses" ? "tabs__content--active" : ""}`}>
                <QuotationResponses
                  quoteRequests={quoteRequests}
                  loading={loading}
                  setActiveQuote={setActiveQuote}
                  MAX_NEGOTIATIONS={MAX_NEGOTIATIONS}
                />
              </div>

              <div className={`tabs__content ${activeTab === "request" ? "tabs__content--active" : ""}`}>
                <QuoteRequestForm userId={userId} />
              </div>
            </div>
          )}
        </div>

        {(historyVisible || (isMobile && showHistory)) && (
          <PreviousHistory
            historyQuotes={historyQuotes}
            loading={loading}
            setActiveQuote={setActiveQuote}
            toggleHistory={toggleHistory}
            isMobile={isMobile}
            userId={userId}
            activeSession={activeSession}
            showHistory={showHistory}
          />
        )}
      </div>
    </div>
  )
}
